/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, DoCheck, OnDestroy, OnInit} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {AlertModalService, ConfirmationModalService} from '../../common/component/modal';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PDF_CREATION_METHOD} from '../../common/constants';
import {ParentPlanType} from '../../common/models/parent-plan-type.model';
import {NgEventBus} from 'ng-event-bus';
import {PlanLodgementType} from '../../common/models/plan-lodgement-type.model';
import {PlanPurposeLookup} from '../../common/models/plan-purpose.model';
import {FormerTenureCardinality} from '../../common/models/former-tenure-cardinality.model';
import {FormerTenureSubjectOfPlan} from '../../common/models/former-tenure-subject-of-plan.model';
import {PlanTypeLookup} from '../../common/models/plan-type.model';
import {UiConfiguration} from '../../common/models/ui-configuration.model';
import {FormerTenureUiConfig} from '../../common/models/former-tenure-ui-config';
import {FormerTenureValidity} from '../../common/models/former-tenure-validity.model';
import {SubjectLandType} from '../../common/models/subject-land-type.model';
import {TenureService} from './tenure.service';

import {FormsModule} from '@angular/forms';
import {AffectedTenureComponent} from './affected-tenure/affected-tenure.component';
import {FormerTenureComponent} from './former-tenure-summary/former-tenure.component';
import {TenureModalService} from '../../common/component/modal/tenure-modal/tenure-modal.service';
import {equals, equalsIgnoreAttrsValues, isDefined} from '../../common/service/data-util';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {finalize, first, of, switchMap} from 'rxjs';
import {PermissionsService} from '../../common/service/permissions.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-tenure',
    imports: [FormsModule, AffectedTenureComponent, FormerTenureComponent, NlrHasPermissionsPipe, NlrPermissionsDisabledPipe],
    templateUrl: './tenure.component.html',
    styleUrl: './tenure.component.scss'
})
export class TenureComponent implements OnInit, DoCheck, OnDestroy {
    title = 'TenureComponent';

    private planLodgementTypeLookups: PlanLodgementType[];

    private planPurposeLookups: PlanPurposeLookup[];

    private formerTenureCardinalityLookups: FormerTenureCardinality[];

    private formerTenureSubjectOfPlanLookups: FormerTenureSubjectOfPlan[];

    private planTypeLookups: PlanTypeLookup[];

    private parentPlanTypeLookups: ParentPlanType[];

    private infoUiGroupDisplayLookups: UiConfiguration[];

    private formerTenureUIConfigLookup: FormerTenureUiConfig[];

    private formerTenureValidityLookups: FormerTenureValidity[];

    private subjectLandTypeLookups: SubjectLandType[];

    private editViewInformationPermissions = ['EDIT_INFORMATION', 'VIEW_INFORMATION'];

    get readonly() {
        return this.planModelService.readonly;
    }

    constructor(
        private lookupService: LookupService,
        private planLodgementService: PlanLodgementService,
        private confirmationModalService: ConfirmationModalService,
        private alertModalService: AlertModalService,
        private planModelService: PlanModelService,
        private eventBus: NgEventBus,
        private permissionsService: PermissionsService,
        private tenureService: TenureService,
        private tenureModalService: TenureModalService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit(): void {
        console.debug(`${this.title} - ngOnInit`);

        this.planLodgementTypeLookups = this.lookupService.getLookups<PlanLodgementType>('planLodgementType');
        this.planPurposeLookups = this.lookupService.getLookups<PlanPurposeLookup>('planPurpose');
        this.formerTenureCardinalityLookups = this.lookupService.getLookups<FormerTenureCardinality>('formerTenureCardinality');
        this.formerTenureSubjectOfPlanLookups = this.lookupService.getLookups<FormerTenureSubjectOfPlan>('formerTenureSubjectOfPlan');
        this.planTypeLookups = this.lookupService.getLookups<PlanTypeLookup>('planType');
        this.parentPlanTypeLookups = this.lookupService.getLookups<ParentPlanType>('parentPlanType');
        this.infoUiGroupDisplayLookups = this.lookupService.getLookups<UiConfiguration>('infoUiGroupDisplay');
        this.formerTenureUIConfigLookup = this.lookupService.getLookups<FormerTenureUiConfig>('formerTenureUiConfig');
        this.formerTenureValidityLookups = this.lookupService.getLookups<FormerTenureValidity>('formerTenureValidity');
        this.subjectLandTypeLookups = this.lookupService.getLookups<SubjectLandType>('subjectLandType', true);

        this.eventBus
            .on('openAffectedLotModal')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.edit(true));
    }

    previousPurpose;
    previousFormerTenureSubject;
    formerTenuresTemp = [];
    summaryViewModel = {tenures: []};

    getModel() {
        return this.planModelService.getModel();
    }

    getPdfCreationMethod() {
        return this.getModel().pdfCreationMethod;
    }

    isPortalGenerated() {
        return this.getModel().pdfCreationMethod == PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }

    isCadPlan() {
        return this.getPdfCreationMethod() == PDF_CREATION_METHOD.CAD_SOFTWARE;
    }

    isDigitalPlan() {
        return this.getPdfCreationMethod() == PDF_CREATION_METHOD.DIGITAL_PLAN;
    }

    isFormerTenureDisplay(formerTenureDisplay) {
        const lookup = this.getPlanPurposesLookup();
        if (lookup) {
            return lookup.parentTenureAssociationType === formerTenureDisplay;
        }
        return false;
    }

    getTenureDisplayType() {
        let tenureDisplayType;
        if (this.showAffectedTenure()) {
            tenureDisplayType = 'AFFECTED_TENURE';
        } else if (this.showFormerTenureCad()) {
            tenureDisplayType = 'FORMER_TENURE_CAD';
        } else if (this.showFormerTenurePortal()) {
            tenureDisplayType = 'FORMER_TENURE_PORTAL';
        } else if (this.showFormerTenureDigital()) {
            tenureDisplayType = 'FORMER_TENURE_DIGITAL';
        }
        return tenureDisplayType;
    }

    gotPrerequisites() {
        return (
            this.getModel().lodgementType &&
            this.getModel().tenureType &&
            this.getModel().planType &&
            this.getModel().planNumber &&
            this.getModel().planPurpose &&
            this.getModel().pdfCreationMethod &&
            this.planLodgementTypeLookups &&
            this.planPurposeLookups &&
            this.planTypeLookups &&
            this.parentPlanTypeLookups &&
            this.formerTenureCardinalityLookups &&
            this.formerTenureSubjectOfPlanLookups &&
            this.formerTenureValidityLookups &&
            this.infoUiGroupDisplayLookups &&
            this.formerTenureUIConfigLookup &&
            this.getFormerTenureValidityLookup() &&
            this.getFormerTenureCardinalityLookup() &&
            this.getFormerTenureCardinalityLookup().maxFormerTenure > 0 &&
            this.subjectLandTypeLookups
        );
    }

    showAffectedTenure() {
        return this.isFormerTenureDisplay('AFFECTED_TENURE');
    }

    showFormerTenurePortal() {
        return this.isPortalGenerated() && this.isFormerTenureDisplay('FORMER_TENURE');
    }

    showFormerTenureCad() {
        return this.isCadPlan() && this.isFormerTenureDisplay('FORMER_TENURE');
    }

    showFormerTenureDigital() {
        return this.isDigitalPlan() && this.isFormerTenureDisplay('FORMER_TENURE');
    }

    getPlanLodgementTypeLookup() {
        if (this.planLodgementTypeLookups) {
            return this.planLodgementTypeLookups.find(l => l.name === this.getModel().lodgementType.value);
        }
        return undefined;
    }

    getInfoUiGroupDisplayLookup() {
        return this.infoUiGroupDisplayLookups.find(l => l.planPurposeCode === this.getModel().planPurpose.id);
    }

    getFormerTenureUIConfigLookup() {
        return this.formerTenureUIConfigLookup.find(l => l.parentTenureAssociationType === this.getPlanPurposesLookup().parentTenureAssociationType);
    }

    getPlanPurposesLookup() {
        return this.planPurposeLookups.find(l => l.id === this.getModel().planPurpose.id);
    }

    labelMap = {};
    getLabelMap() {
        const labelMap = this.tenureService.getLabelMap(this.getFormerTenureUIConfigLookup());
        if (!labelMap !== this.labelMap) {
            this.labelMap = labelMap;
        }
        return this.labelMap;
    }

    getFormerTenureCardinalityLookup() {
        return this.formerTenureCardinalityLookups.find(
            lookup =>
                this.getModel().tenureType &&
                lookup.tenureType === this.getModel().tenureType.name &&
                this.getModel().planType &&
                lookup.planType === this.getModel().planType.name &&
                this.getModel().planPurpose &&
                lookup.planPurpose === this.getModel().planPurpose.name
        );
    }

    getFormerTenureValidityLookup() {
        return this.formerTenureValidityLookups.find(
            lookup =>
                this.getModel().tenureType &&
                lookup.tenureType === this.getModel().tenureType.name &&
                this.getModel().planType &&
                lookup.planType === this.getModel().planType.name &&
                this.getModel().planPurpose &&
                lookup.planPurpose === this.getModel().planPurpose.name
        );
    }

    applicableFormerTenureSubjectOfPlanLookups = [];

    getApplicableFormerTenureSubjectOfPlanLookups() {
        this.applicableFormerTenureSubjectOfPlanLookups = [];
        const formerTenureCardinalityLookup = this.getFormerTenureCardinalityLookup();
        if (formerTenureCardinalityLookup && formerTenureCardinalityLookup.subjectOfPlans) {
            const subjectOfPlanCodes = formerTenureCardinalityLookup.subjectOfPlans.split(',');

            Array.prototype.push.apply(
                this.applicableFormerTenureSubjectOfPlanLookups,
                this.formerTenureSubjectOfPlanLookups.filter(
                    formerTenureSubjectOfPlanLookup => subjectOfPlanCodes.indexOf(formerTenureSubjectOfPlanLookup.code) !== -1
                )
            );

            if (this.applicableFormerTenureSubjectOfPlanLookups.length) {
                if (this.getModel().formerTenureSubject && subjectOfPlanCodes.indexOf(this.getModel().formerTenureSubject.code) !== -1) {
                    this.getModel().formerTenureSubject =
                        this.applicableFormerTenureSubjectOfPlanLookups.find(v => v.id === this.getModel().formerTenureSubject.id) ||
                        this.getModel().formerTenureSubject;
                } else {
                    this.getModel().formerTenureSubject = this.applicableFormerTenureSubjectOfPlanLookups[0];
                }
            }
        }
        if (this.applicableFormerTenureSubjectOfPlanLookups.length === 0) {
            this.getModel().formerTenureSubject = null;
        }
        return this.applicableFormerTenureSubjectOfPlanLookups;
    }

    createSummaryViewModel() {
        return this.tenureService.toSummaryViewModel(
            this.getModel(),
            this.parentPlanTypeLookups,
            this.subjectLandTypeLookups,
            this.getFormerTenureUIConfigLookup()
        );
    }

    edit(saveAfterEdit: boolean) {
        this.tenureService.data.parentLotResponseMap = this.tenureService.data.parentLotResponseMap || {};
        let filteredParentPlanTypeLookups = [];
        let filteredSubjectLandTypeLookups = [];

        if (this.getFormerTenureValidityLookup()) {
            const parentPlanTypes = this.getFormerTenureValidityLookup().parentPlanType;

            filteredParentPlanTypeLookups = this.parentPlanTypeLookups.filter(v => parentPlanTypes && parentPlanTypes.indexOf(v.code) > -1);

            const subjectLandTypes = this.getFormerTenureValidityLookup().subjectLandTypes;

            if (subjectLandTypes) {
                filteredSubjectLandTypeLookups = this.subjectLandTypeLookups.filter(
                    v => v.active && parentPlanTypes && subjectLandTypes.indexOf(v.id) > -1
                );
            }
        }
        this.tenureModalService
            .showModal({
                tenureDisplayType: this.getTenureDisplayType(),
                formerTenureValidityLookup: this.getFormerTenureValidityLookup(),
                formerTenureUIConfigLookup: this.getFormerTenureUIConfigLookup(),
                parentPlanTypeLookups: filteredParentPlanTypeLookups,
                subjectLandTypeLookups: filteredSubjectLandTypeLookups
            })
            .pipe(first())
            .subscribe(data => {
                if (typeof data === 'object') {
                    const formerTenures = this.getModel().formerTenures;
                    const oldTenures = formerTenures.slice();
                    if (this.getModel().planPurpose.parentTenureAssociationType === 'AFFECTED_TENURE') {
                        const updates = data.formerTenures.filter(t => t.id);
                        const updateIds = updates.map(t => t.id);

                        // Remove unwanted tenures, replace updated tenures, add new tenures
                        formerTenures.splice(
                            0,
                            formerTenures.length,
                            ...formerTenures
                                .filter(t => updateIds.includes(t.id))
                                .map(t => {
                                    const update = updates.find(u => u.id === t.id);
                                    const updateViewModel = this.tenureService.toDetailViewModel({formerTenures: [update]});
                                    const tenureViewModel = this.tenureService.toDetailViewModel({formerTenures: [t]});
                                    return equalsIgnoreAttrsValues(updateViewModel, tenureViewModel, ['id'], [null, undefined]) ? t : update;
                                }),
                            ...data.formerTenures.filter(t => !t.id)
                        );
                    } else {
                        formerTenures.splice(0, formerTenures.length, ...data.formerTenures);
                    }

                    this.getModel().spatialDependencyCalcDateTime = undefined;

                    this.summaryViewModel = this.createSummaryViewModel();

                    const formerTenureChanged = this.tenureService.formerTenureChanged(oldTenures, this.getModel().formerTenures);

                    if (saveAfterEdit) {
                        this.save(formerTenureChanged);
                    } else if (formerTenureChanged) {
                        this.planModelService.populateFormerTenureNotifications = true;
                    }
                }
            });
    }

    private save(formerTenureChanged: boolean) {
        const subscription = this.planLodgementService
            .save({showAlertOnSave: false})
            .pipe(
                first(),
                finalize(() => {
                    if (formerTenureChanged) {
                        this.planModelService.populateFormerTenureNotifications = true;
                    }
                })
            )
            .subscribe();
        this.eventBus.cast('nlr:busy', subscription);
    }

    isEditDisabled() {
        return (
            this.readonly ||
            (this.getModel().formerTenureSubject && this.getModel().formerTenureSubject.disableAddFormerTenure) ||
            this.permissionsService.isDisabledByPermissions(this.editViewInformationPermissions)
        );
    }

    removeLot(tenureIndex, lotIndex) {
        const planModel = this.planModelService.getModel();

        if (
            planModel.planPurpose.parentTenureAssociationType === 'AFFECTED_TENURE' &&
            planModel.interests.some(interest =>
                interest.burdenedAffectedTenures.some(
                    tenure =>
                        tenure.affectedTenure === this.getModel().formerTenures[tenureIndex] &&
                        tenure.affectedTenureLot === this.getModel().formerTenures[tenureIndex].lots[lotIndex]
                )
            )
        ) {
            const subscription = this.confirmationModalService
                .showModal({
                    title: 'Warning',
                    confirmationText:
                        'Deleting the affected tenure will result in the deletion of the ' +
                        'affected tenure from all related interests. Do you want to proceed?'
                })
                .pipe(
                    switchMap(action => {
                        if (action === 'yes') {
                            const tenure = this.getModel().formerTenures[tenureIndex];
                            let formerTenureChanged = false;
                            if (tenure.lots.length === 1) {
                                const oldTenures = this.getModel().formerTenures.slice();
                                this.getModel().formerTenures.splice(tenureIndex, 1);
                                formerTenureChanged = this.tenureService.formerTenureChanged(oldTenures, this.getModel().formerTenures);
                            } else {
                                tenure.lots.splice(lotIndex, 1);
                            }
                            this.summaryViewModel = this.createSummaryViewModel();
                            return this.planLodgementService.save({showAlertOnSave: false}).pipe(
                                first(),
                                finalize(() => {
                                    if (formerTenureChanged) {
                                        this.planModelService.populateFormerTenureNotifications = true;
                                    }
                                })
                            );
                        }
                        return of(null);
                    })
                )
                .subscribe({
                    error: (err: any) => this.alertModalService.createAlertMessage(err),
                    complete: () => subscription.unsubscribe()
                });
        } else {
            const tenure = this.getModel().formerTenures[tenureIndex];

            let formerTenureChanged = false;

            if (tenure.lots.length === 1) {
                const oldTenures = this.getModel().formerTenures.slice();
                this.getModel().formerTenures.splice(tenureIndex, 1);
                formerTenureChanged = this.tenureService.formerTenureChanged(oldTenures, this.getModel().formerTenures);
            } else {
                tenure.lots.splice(lotIndex, 1);
            }

            this.summaryViewModel = this.createSummaryViewModel();

            const subscription = this.planLodgementService
                .save({showAlertOnSave: false})
                .pipe(
                    first(),
                    finalize(() => {
                        if (formerTenureChanged) {
                            this.planModelService.populateFormerTenureNotifications = true;
                        }
                    })
                )
                .subscribe({
                    error: (err: any) => this.alertModalService.createAlertMessage(err)
                });

            this.eventBus.cast('nlr:busy', subscription);
        }
    }

    removeLotDisabled() {
        return this.readonly || this.getFormerTenureValidityLookup().prePopParentPlan || this.isEditDisabled();
    }

    removeTenure(tenureIndex) {
        if (
            this.getModel().planPurpose.parentTenureAssociationType === 'AFFECTED_TENURE' &&
            this.getModel().interests.some(interest =>
                interest.burdenedAffectedTenures.some(tenure => tenure.affectedTenure === this.getModel().formerTenures[tenureIndex])
            )
        ) {
            const subscription = this.confirmationModalService
                .showModal({
                    title: 'Warning',
                    confirmationText:
                        'Deleting the affected tenure will result in the deletion of the affected ' +
                        'tenure from all related interests. Do you want to proceed?'
                })
                .pipe(
                    switchMap(action => {
                        if (action === 'yes') {
                            const oldTenures = this.getModel().formerTenures.slice();
                            this.getModel().formerTenures.splice(tenureIndex, 1);
                            const formerTenureChanged = this.tenureService.formerTenureChanged(oldTenures, this.getModel().formerTenures);
                            this.summaryViewModel = this.createSummaryViewModel();
                            this.getModel().spatialDependencyCalcDateTime = undefined;
                            return this.planLodgementService.save({showAlertOnSave: false}).pipe(
                                first(),
                                finalize(() => {
                                    if (formerTenureChanged) {
                                        this.planModelService.populateFormerTenureNotifications = true;
                                    }
                                })
                            );
                        } else {
                            return of(null);
                        }
                    })
                )
                .subscribe({
                    error: (err: any) => this.alertModalService.createAlertMessage(err),
                    complete: () => subscription.unsubscribe()
                });
        } else {
            const oldTenures = this.getModel().formerTenures.slice();
            this.getModel().formerTenures.splice(tenureIndex, 1);
            const formerTenureChanged = this.tenureService.formerTenureChanged(oldTenures, this.getModel().formerTenures);
            this.summaryViewModel = this.createSummaryViewModel();
            this.getModel().spatialDependencyCalcDateTime = undefined;
            const subscription = this.planLodgementService
                .save({showAlertOnSave: false})
                .pipe(
                    first(),
                    finalize(() => {
                        if (formerTenureChanged) {
                            this.planModelService.populateFormerTenureNotifications = true;
                        }
                    })
                )
                .subscribe({
                    error: (err: any) => this.alertModalService.createAlertMessage(err)
                });
            this.eventBus.cast('nlr:busy', subscription);
        }
    }

    removeTenureDisabled() {
        return this.readonly || this.getFormerTenureValidityLookup().prePopParentPlan || this.isEditDisabled();
    }

    tenureCardinalityErrorMessage() {
        const tenureCardinality = this.tenureService.getTenureCardinality(this.getModel());
        const tenureCardinalityLookup = this.getFormerTenureCardinalityLookup();

        if (
            (tenureCardinality < tenureCardinalityLookup.minFormerTenure || tenureCardinality > tenureCardinalityLookup.maxFormerTenure) &&
            !(tenureCardinality === 0 && this.getModel().formerTenureSubject && this.getModel().formerTenureSubject.code === 'CP')
        ) {
            return tenureCardinalityLookup.errorMessage;
        }
        return undefined;
    }

    ngDoCheck() {
        if (!this.gotPrerequisites()) {
            return;
        }
        if (this.getModel().purpose !== this.previousPurpose && isDefined(this.previousPurpose)) {
            this.previousPurpose = this.getModel().purpose;
            this.getModel().formerTenures.length = 0;
        }
        if (this.getModel().formerTenureSubject) {
            if (this.getModel().formerTenureSubject.id !== this.previousFormerTenureSubject) {
                this.previousFormerTenureSubject = this.getModel().formerTenureSubject.id;
                if (equals(this.getModel().formerTenureSubject.id, 'CP')) {
                    this.formerTenuresTemp = [...this.getModel().formerTenures];
                    this.getModel().formerTenures.length = 0;
                } else {
                    if (this.formerTenuresTemp.length > 0) {
                        this.getModel().formerTenures = [...this.formerTenuresTemp];
                        this.formerTenuresTemp.length = 0;
                    }
                }
            }
        }

        const summaryViewModel = this.createSummaryViewModel();
        if (!equals(summaryViewModel, this.summaryViewModel)) {
            this.summaryViewModel = summaryViewModel;
        }
    }

    ngOnDestroy() {
        if (this.getModel().formerTenures && this.getModel().formerTenures.length) {
            this.getModel().formerTenures.length = 0;
        }
        if (this.getModel().formerTenureSubjectOfPlan) {
            delete this.getModel().formerTenureSubjectOfPlan;
        }
    }

    isValidated() {
        return this.planModelService.validated;
    }
}
